import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { AppMenu } from './AppMenu';
import { AppProfile } from './AppProfile';
import { AppTopbar } from './AppTopbar';

import api from './api/api';
import { MensagemContext } from './contexts/MensagemContext';
import { AuthContext } from './contexts/AuthContext';

import Logo from '../src/assets/logo_conservatorio.png'

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'prismjs/themes/prism-coy.css';
import './layout/flags/flags.css';
import './layout/layout.scss';

const MenuMain = ({ children }) => {
    const toast = useRef(null);
    const [layoutMode] = useState('static');
    const [staticMenuInactive, setStaticMenuInactive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const [filteredMenu, setFilteredMenu] = useState([]);
    const sidebar = useRef();
    const { user, permissoes } = useContext(AuthContext);
    const { mensagemLida } = useContext(MensagemContext);
    const [mensagemNaoLida, setMensagemNaoLida] = useState(0);
    const history = useHistory();

    let menuClick = false;

    const getMensagemNaoLida = useCallback(async () => {
        try {
            const response = await api.get(`mensagens/mensagemNaoLida/${user.email}`);
            setMensagemNaoLida(response.data.length);
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro ao obter mensagens',
                detail: error.toString(),
            });
        }
    }, [user.email, mensagemLida]);

    useEffect(() => {
        getMensagemNaoLida();

        const interval = setInterval(() => {
            getMensagemNaoLida();
        }, 60000);

        return () => clearInterval(interval);
    }, [getMensagemNaoLida]);

    useEffect(() => {
        if (!permissoes) return;

        const menu = [
            { label: "Licenças", icon: "pi pi-folder-open", to: "/licenca" },
            { label: "Campanhas", icon: "pi pi-calendar", to: "/campanhas" },
            { label: "Inscrições", icon: "pi pi-folder-open", to: "/inscricoes" },
            { label: "Perfis", icon: "pi pi-id-card", to: "/perfis" },
            { label: "Perfis Duplicados", icon: "pi pi-id-card", to: "/perfisDuplicados" },
            { label: "Histórico Alunos", icon: "pi pi-id-card", to: "/historicoPerfil" },
            { label: "Fila Instrumentos", icon: "pi pi-id-card", to: "/filaInstrumentos" },
            { label: "Tipo Cursos", icon: "pi pi-folder-open", to: "/tipoCursos" },
            { label: "Cursos", icon: "pi pi-folder-open", to: "/cursos" },
            { label: "Turmas", icon: "pi pi-folder-open", to: "/turmas" },
            { label: "Matrículas", icon: "pi pi-id-card", to: "/matriculas" },
            { label: "Periodo Matricula", icon: "pi pi-calendar", to: "/periodoMatricula" },
            { label: "Planejamentos", icon: "pi pi-book", to: "/planejamentos" },
            { label: "Mensagens", icon: "pi pi-id-card", icon2: "pi pi-envelope", contador: mensagemNaoLida.toString(), to: "/mensagens" },
            { label: "Logs", icon: "pi pi-id-card", to: "/logs" },
            { label: "Rotas", icon: "pi pi-arrow-circle-right", to: "/rotas" },
            { label: "Permissões", icon: "pi pi-id-card", to: "/permissoes" },
        ];

        // Filtra itens do menu baseado na permissão de visualização
        const updatedMenu = menu.filter(item => {
            const key = item.to.replace('/', '');
            return permissoes[key]?.visualizar; // somente mostra se tem visualizar = true
        });

        // Se tiver alguma role de super-admin no backend, pode manter tudo
        const isSuperAdmin = permissoes?.superAdmin; // ou outro campo que identifique super-admin
        setFilteredMenu(isSuperAdmin ? menu : updatedMenu);

    }, [permissoes, mensagemNaoLida]);


    const onWrapperClick = () => {
        if (!menuClick) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
        menuClick = false;
    };

    const onToggleMenu = (event) => {
        menuClick = true;

        if (isDesktop()) {
            if (layoutMode === 'overlay') {
                setOverlayMenuActive((prev) => !prev);
            } else if (layoutMode === 'static') {
                setStaticMenuInactive((prev) => !prev);
            }
        } else {
            setMobileMenuActive((prev) => !prev);
        }
        event.preventDefault();
    };

    const onSidebarClick = () => {
        menuClick = true;
    };

    const onMenuItemClick = (event) => {
        if (!event.item.items) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
    };

    const isDesktop = () => window.innerWidth > 1024;

    const isSidebarVisible = () => {
        if (isDesktop()) {
            if (layoutMode === 'static') return !staticMenuInactive;
            if (layoutMode === 'overlay') return overlayMenuActive;
        }
        return true;
    };

    const addClass = (element, className) => {
        element.classList?.add(className);
    };

    const removeClass = (element, className) => {
        element.classList?.remove(className);
    };

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, 'body-overflow-hidden');
        } else {
            removeClass(document.body, 'body-overflow-hidden');
        }
    }, [mobileMenuActive]);

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
    });

    const sidebarClassName = classNames('layout-sidebar', 'layout-sidebar-light');

    return (
        <div className={wrapperClass} onClick={onWrapperClick}>
            <AppTopbar onToggleMenu={onToggleMenu} />
            <CSSTransition classNames="layout-sidebar" timeout={{ enter: 200, exit: 200 }} in={isSidebarVisible()} unmountOnExit>
                <div ref={sidebar} className={sidebarClassName} onClick={onSidebarClick}>
                    <div className="layout-Logo" style={{ cursor: 'pointer', padding: '10px 10px' }} onClick={() => history.push('/admin')}>
                        {/* <img alt="Logo" src="https://conservatorio.patosdeminas.mg.gov.br/static/media/logo_conservatorio.b0757b22.jpg" width="100%" height="auto" style={{ maxWidth: '250px' }} /> */}

                        <img alt="Logo" src={Logo} width="100%" height="auto" style={{ maxWidth: '250px' }} />
                    </div>
                    <AppProfile />
                    <AppMenu model={filteredMenu} onMenuItemClick={onMenuItemClick} />
                </div>
            </CSSTransition>

            <div className="layout-main">
                <div className="p-col-12">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default MenuMain;
